import React, { useRef, useEffect } from 'react';
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import ChatInput from './ChatInput';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const MarkdownRenderer = ({ content, className = "" }) => {
  const markdownComponents = {
    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
    li: ({ children }) => <li className="mb-1">{children}</li>,
    code: ({ children, className: codeClass }) =>
      codeClass ? (
        <pre className="bg-gray-100 text-gray-800 p-2 rounded text-xs overflow-x-auto mb-2 border border-gray-200">
          <code>{children}</code>
        </pre>
      ) : (
        <code className="bg-gray-100 px-1 py-0.5 rounded text-xs text-gray-700 border border-gray-200">{children}</code>
      ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-2 text-gray-600">{children}</blockquote>
    ),
    h1: ({ children }) => <h1 className="text-lg font-bold mb-2 text-gray-900">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-bold mb-2 text-gray-900">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-bold mb-1 text-gray-900">{children}</h3>,
    h4: ({ children }) => <h4 className="text-sm font-semibold mb-1 text-gray-900">{children}</h4>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    hr: () => <hr className="border-gray-200 my-3" />,
    table: ({ children }) => (
      <table className="w-full text-xs border-collapse border border-gray-200 mb-2">
        {children}
      </table>
    ),
    th: ({ children }) => (
      <th className="border border-gray-200 px-2 py-1 bg-gray-50 font-semibold text-left text-gray-900">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-gray-200 px-2 py-1 text-gray-700">{children}</td>
    ),
  };

  return (
    <div className={`text-sm prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown components={markdownComponents}>
        {content || ''}
      </ReactMarkdown>
    </div>
  );
};

const ChatSidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  title = "Assistant",
  chatHistory = [],
  message,
  setMessage,
  sendMessage,
  loading = false,
  isTyping = false,
  streamingMessage = '',
  suggestedActions = [],
  handleQuickAction,
  showActionButtons = false,
  customWidth = "w-1/4",
  hideBubble = false,
  isTable = false
}) => {
  const chatEndRef = useRef(null);

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, streamingMessage]);

  return (
    <div className={classNames(
      isTable ? "bg-white " : "",
      isSidebarOpen ? "bg-white border-r border-gray-200" : "",
      " flex flex-col transition-all duration-300 ease-in-out relative h-full",
      isSidebarOpen ? customWidth : "w-[1px]"
    )}>
      {
        hideBubble ? null : (

          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="absolute -right-3 top-4 z-20 bg-white border border-gray-200 rounded-full p-1 shadow-md hover:shadow-lg transition-shadow"
          >
            {isSidebarOpen ? (
              <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-gray-600" />
            )}
          </button>
        )
      }

      {/* Header - Fixed */}
      {isSidebarOpen && title && (
        <div className="flex-shrink-0 p-4 border-b border-gray-200 pb-3 bg-white">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
        </div>
      )}

      {/* Chat Messages - Scrollable */}
      {isSidebarOpen && (
        <div className="flex-1 overflow-y-auto p-4 min-h-0 bg-white">
          {chatHistory.length === 0 ? null : (
            <div className="space-y-4">
              {Array.isArray(chatHistory) && chatHistory.map((msg, index) => (
                <div key={index}>
                  <div
                    className={classNames(
                      'flex flex-col',
                      msg.role === 'user' ? 'items-end' : 'items-start'
                    )}
                  >
                    <div
                      className={classNames(
                        'max-w-sm px-4 py-3',
                        msg.role === 'user'
                          ? 'bg-gray-100 text-gray-900 rounded-lg border border-gray-200'
                          : 'text-gray-900'
                      )}
                    >
                      {msg.role === 'assistant' ? (
                        <MarkdownRenderer content={msg.content} />
                      ) : (
                        <p className="text-sm">{msg.content || ''}</p>
                      )}

                      {/* Enhanced assistant metadata */}
                      {msg.metadata && (
                        <div className="mt-2 space-y-1">
                          {msg.metadata.status && msg.metadata.status !== 'completed' && (
                            <div className="flex items-center space-x-1">
                              <div className={classNames(
                                'w-2 h-2 rounded-full',
                                msg.metadata.status === 'thinking' ? 'bg-blue-500 animate-pulse' :
                                  msg.metadata.status === 'waiting_user' ? 'bg-yellow-500' :
                                    msg.metadata.status === 'error' ? 'bg-red-500' : 'bg-green-500'
                              )}></div>
                              <span className="text-xs opacity-75">{msg.metadata.status}</span>
                            </div>
                          )}

                          {msg.metadata.suggestedActions && msg.metadata.suggestedActions.length > 0 && (
                            <div className="text-xs opacity-75 mt-1">
                              💡 {msg.metadata.suggestedActions.length} suggested action{msg.metadata.suggestedActions.length > 1 ? 's' : ''} available
                            </div>
                          )}
                        </div>
                      )}

                     
                    </div>
                    {msg.role === 'assistant' && index < chatHistory.length - 1 && (
                      <div className="w-full h-px bg-gray-200 mt-4"></div>
                    )}
                  </div>

                  {/* Plan Preview */}
                  {msg.planData && (
                    <div className="mt-2 ml-0 max-w-lg">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-900">{msg.planData.title}</h4>
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {msg.planData.totalSteps} steps
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${msg.planData.complexity === 'high' ? 'bg-red-100 text-red-800' :
                              msg.planData.complexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                              {msg.planData.complexity}
                            </span>
                          </div>
                        </div>

                        {/* Conversational Plan Description */}
                        <div className="text-sm text-gray-800 mb-4 whitespace-pre-line leading-relaxed">
                          {msg.planData.description}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Suggested Actions */}
              {suggestedActions && suggestedActions.length > 0 && handleQuickAction && (
                <div className="mt-4">
                  <div className="grid grid-cols-1 gap-3">
                    {suggestedActions.slice().reverse().map((action, index) => {
                      const originalIndex = suggestedActions.length - 1 - index;
                      return (
                        <button
                          key={originalIndex}
                          onClick={() => handleQuickAction(action)}
                          disabled={loading}
                          className={
                            originalIndex === 0 
                              ? "w-full px-4 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transition-all duration-200" 
                              : "w-full px-4 py-3 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 border border-gray-200 disabled:opacity-50 flex items-center justify-center space-x-2 transition-all duration-200"
                          }
                        >
                          {originalIndex === 0 && (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          <span className="text-center">{action}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Streaming message indicator */}
              {isTyping && streamingMessage && (
                <div className="flex flex-col items-start">
                  <div className="max-w-sm px-4 py-3 text-gray-900">
                    <div className="flex items-center space-x-2 mb-1">
                      <ArrowPathIcon className="h-3 w-3 text-gray-600 animate-spin" />
                      <span className="text-xs text-gray-500">Assistant is typing...</span>
                    </div>
                    <MarkdownRenderer content={streamingMessage} />
                  </div>
                  <div className="w-full h-px bg-gray-200 mt-4"></div>
                </div>
              )}

              {/* Typing indicator without message */}
              {isTyping && !streamingMessage && (
                <div className="flex flex-col items-start">
                  <div className="px-4 py-3 text-gray-900">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-xs text-gray-500">Thinking...</span>
                    </div>
                  </div>
                  <div className="w-full h-px bg-gray-200 mt-4"></div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>
          )}
        </div>
      )}

      {/* Input Area - Fixed at bottom */}
      {isSidebarOpen && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
          <ChatInput
            value={message}
            onChange={setMessage}
            sendMessage={sendMessage}
            loading={loading || isTyping}
            isTable={isTable}
          />
        </div>
      )}
    </div>
  );
};

export default ChatSidebar;